import { message } from "antd";
import { type ReactNode, useCallback, useState } from "react";

import { IconX } from "@btnet/blueprint-icons";

import { useIsTouchDevice } from "utilities/useIsTouchDevice";

import { BTBottomSheet } from "commonComponents/btWrappers/BTBottomSheet/BTBottomSheet";
import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";
import { BTExternalLink } from "commonComponents/btWrappers/BTExternalLink/BTExternalLink";
import { BTLink } from "commonComponents/btWrappers/BTLink/BTLink";

/**
 * Defines what the "open maps" action does, which differs by surface:
 * - `url`: link out to Google Maps (used for contacts and read-only addresses
 *   that expose a maps URL).
 * - `modal`: open the in-app MapModal showing the saved lat/long pin (used on
 *   Job Details, where the address exposes a manually-placed map pin rather
 *   than a maps URL).
 */
export type AddressMapAction =
    | { kind: "url"; mapsUrl: string }
    | { kind: "modal"; onOpenMap: () => void };

export interface IAddressActionSheetProps {
    /** Plain-text address copied to the clipboard and shown in the sheet header. */
    copyText: string;
    /** Trigger content (address text or a map-pin icon). */
    children: ReactNode;
    /** Stable testid base; "{base}", "{base}-maps", "{base}-copy", "{base}-close" are emitted. */
    testIdBase: string;
    /** What "open maps" does on this surface — link out, or open the in-app MapModal. */
    mapAction: AddressMapAction;
}

const actionRowClassName =
    "block w-full p-sm text-center rounded-base bg-gray-5 text-fg-default hover:bg-gray-15 focus:bg-gray-15";

/**
 * Wraps a read-only address (or a map-pin trigger) so it can be both opened in
 * maps and copied as text.
 *
 * On touch-primary devices (phones and tablets — matched by the
 * `(pointer: coarse)` media query) tapping the trigger opens a BTBottomSheet
 * exposing an "open maps" action and "Copy address". Without the sheet the
 * trigger intercepts every tap (a maps link, or a pin that opens the MapModal),
 * leaving no way to select and copy the address — the reason this component
 * exists. The sheet mirrors the PhoneActionSheet pattern used for contact phone
 * numbers.
 *
 * On mouse-primary devices it collapses to the bare trigger (a maps link for
 * `url`, or a click-to-open-map element for `modal`), preserving existing
 * desktop behavior; desktop users can select the text directly.
 */
export function AddressActionSheet({
    copyText,
    children,
    testIdBase,
    mapAction,
}: IAddressActionSheetProps) {
    const isTouchPrimary = useIsTouchDevice();
    const [isSheetOpen, setIsSheetOpen] = useState(false);

    const openSheet = useCallback(() => setIsSheetOpen(true), []);
    const closeSheet = useCallback(() => setIsSheetOpen(false), []);

    const handleCopy = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(copyText);
            void message.success("Copied");
        } catch {
            void message.error("Unable to copy");
        }
        closeSheet();
    }, [copyText, closeSheet]);

    if (!isTouchPrimary) {
        return mapAction.kind === "url" ? (
            <BTExternalLink href={mapAction.mapsUrl} data-testid={testIdBase} className="w-fit">
                {children}
            </BTExternalLink>
        ) : (
            <BTLink
                to=""
                data-testid={testIdBase}
                isUnderline={false}
                onClick={mapAction.onOpenMap}
                className="p-0 min-w-0 w-fit text-left"
            >
                {children}
            </BTLink>
        );
    }

    return (
        <>
            <BTLink
                to=""
                data-testid={testIdBase}
                isUnderline={false}
                onClick={openSheet}
                className="p-0 min-w-0 w-fit text-left"
            >
                {children}
            </BTLink>
            <BTBottomSheet visible={isSheetOpen} onClose={closeSheet}>
                <div className="flex flex-col gap-xs p-sm pb-12">
                    <div className="flex items-start gap-sm p-sm">
                        {/* Invisible mirror of the close button keeps the centered
                            title clear of the X on both sides without a magic width. */}
                        <BTButton
                            data-testid={`${testIdBase}-close-spacer`}
                            aria-hidden
                            tabIndex={-1}
                            type="tertiary"
                            icon={<IconX />}
                            className="shrink-0 invisible"
                        />
                        <div className="flex-1 min-w-0 font-semibold text-fg-default text-center break-words">
                            {copyText}
                        </div>
                        <BTButton
                            data-testid={`${testIdBase}-close`}
                            aria-label="Close"
                            type="tertiary"
                            icon={<IconX />}
                            onClick={closeSheet}
                            className="shrink-0"
                        />
                    </div>
                    {mapAction.kind === "url" ? (
                        <BTExternalLink
                            href={mapAction.mapsUrl}
                            className={actionRowClassName}
                            onClick={closeSheet}
                            data-testid={`${testIdBase}-maps`}
                        >
                            Open in Maps
                        </BTExternalLink>
                    ) : (
                        <BTLink
                            to=""
                            isUnderline={false}
                            className={actionRowClassName}
                            onClick={() => {
                                closeSheet();
                                mapAction.onOpenMap();
                            }}
                            data-testid={`${testIdBase}-maps`}
                            type="secondary"
                        >
                            View on map
                        </BTLink>
                    )}
                    <BTLink
                        to=""
                        isUnderline={false}
                        className={actionRowClassName}
                        onClick={handleCopy}
                        data-testid={`${testIdBase}-copy`}
                        type="secondary"
                    >
                        Copy address
                    </BTLink>
                </div>
            </BTBottomSheet>
        </>
    );
}
